import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { StructuredAssessment } from '@/components/AssessmentCard';

// ---- Shared query keys -------------------------------------------------------

export const STRUCTURED_ASSESSMENTS_KEY = 'structured-assessments' as const;
export const PROGRESS_ASSESSMENTS_KEY   = 'progress-assessments'   as const;

// ---- Fetch hook --------------------------------------------------------------

export function useStructuredAssessments(userId: string | undefined, isAdmin = false) {
  return useQuery<StructuredAssessment[]>({
    queryKey: [STRUCTURED_ASSESSMENTS_KEY, userId, isAdmin],
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async () => {
      let query = supabase
        .from('assessments')
        .select(`
          id, ai_grade, final_grade, fluency_score, justification, extracted_text, status, created_at, possible_duplicate,
          writing_samples (
            title, writing_type, image_url, image_urls,
            pupils (
              first_name, last_name, anonymisation,
              classes ( name, year_groups ( name, key_stages ( name ) ) )
            )
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);
      if (!isAdmin) {
        query = query.eq('assessed_by', userId!);
      }
      const { data, error } = await query;
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any[]) as StructuredAssessment[];
    },
  });
}

// ---- Delete mutation ---------------------------------------------------------

export function useDeleteAssessment(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (assessmentId) => {
      const { data, error } = await supabase
        .from('assessments')
        .delete()
        .eq('id', assessmentId)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Assessment could not be deleted. You may not have permission.');
      }
    },

    // Optimistic: remove from cache immediately
    onMutate: async (assessmentId) => {
      await queryClient.cancelQueries({ queryKey: [STRUCTURED_ASSESSMENTS_KEY, userId] });
      const previous = queryClient.getQueryData<StructuredAssessment[]>([
        STRUCTURED_ASSESSMENTS_KEY,
        userId,
      ]);
      queryClient.setQueryData<StructuredAssessment[]>(
        [STRUCTURED_ASSESSMENTS_KEY, userId],
        (old) => old?.filter((a) => a.id !== assessmentId) ?? [],
      );
      return { previous };
    },

    // Rollback on error
    onError: (_err, _id, context) => {
      const ctx = context as { previous?: StructuredAssessment[] } | undefined;
      if (ctx?.previous) {
        queryClient.setQueryData([STRUCTURED_ASSESSMENTS_KEY, userId], ctx.previous);
      }
    },

    // Always re-sync both pages' caches after settle
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [STRUCTURED_ASSESSMENTS_KEY, userId] });
      queryClient.invalidateQueries({ queryKey: [PROGRESS_ASSESSMENTS_KEY] });
    },
  });
}
